/*
 * @Author: mina.min
 * @Date: 2025-01-20 10:00:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-09-03 20:22:59
 * @FilePath: /OTA-frontend/src/pages/CampaignManagement/CampaignList.tsx
 * @Description: Campaign列表组件
 */
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import {
  createCampaign,
  deleteCampaign,
  getAvailableFirmwareList,
  getCampaignDetail,
  getCampaignList,
  updateCampaign,
} from '../../services/campaign'
import { getModuleNameList, type ModuleNameItem } from '../../services/module'
import { getProjectPhaseNameList, type ProjectPhaseItem } from '../../services/projectPhase'
import type { CampaignItem } from '../../types'
import { formatToCST } from '../../utils'
import './CampaignList.scss'

const { Title } = Typography
const { Option } = Select

const CampaignList: React.FC = () => {
  const [campaignList, setCampaignList] = useState<CampaignItem[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<CampaignItem | null>(null)
  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [form] = Form.useForm()

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)

  // 固件选择相关状态
  const [availableFirmwareList, setAvailableFirmwareList] = useState<
    Array<{
      firmwareId: string
      moduleName: string
      firmwareVersion: number
      remark?: string
    }>
  >([])
  const [selectedFirmwareInfo, setSelectedFirmwareInfo] = useState<
    Array<{
      firmwareId: string
      moduleName: string
      firmwareVersion: number
      remark?: string
    }>
  >([])
  const [selectedFirmwareIds, setSelectedFirmwareIds] = useState<string[]>([])
  const [moduleNameList, setModuleNameList] = useState<ModuleNameItem[]>([])
  const [projectPhaseList, setProjectPhaseList] = useState<ProjectPhaseItem[]>([])
  const [isFirmwareModalVisible, setIsFirmwareModalVisible] = useState(false)
  const [firmwareListLoading, setFirmwareListLoading] = useState(false)

  // 拖拽排序相关状态
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null)
  const selectedListRef = React.useRef<HTMLDivElement | null>(null)

  // 草稿保存相关状态
  const [hasDraft, setHasDraft] = useState(false)
  const draftKey = 'campaign_draft'

  // 草稿保存和恢复函数
  const saveDraft = (selectedFirmwareIds: string[]) => {
    // 获取完整的表单数据，包括空值
    const completeFormData = form.getFieldsValue()

    // 验证草稿内容，避免保存空内容
    const hasFormData = Object.keys(completeFormData || {}).some(
      key =>
        completeFormData[key] !== undefined &&
        completeFormData[key] !== null &&
        completeFormData[key] !== ''
    )
    const hasFirmwareIds = (selectedFirmwareIds || []).length > 0

    // 只有当有实际内容时才保存草稿
    if (hasFormData || hasFirmwareIds) {
      const draft = {
        formData: completeFormData, // 保存完整的表单数据
        selectedFirmwareIds,
        timestamp: Date.now(),
      }
      localStorage.setItem(draftKey, JSON.stringify(draft))
      setHasDraft(true)
    }
  }

  const loadDraft = () => {
    const draftStr = localStorage.getItem(draftKey)
    if (draftStr) {
      try {
        const draft = JSON.parse(draftStr)
        // 检查草稿是否过期（24小时）
        if (Date.now() - draft.timestamp < 24 * 60 * 60 * 1000) {
          // 先设置表单数据
          form.setFieldsValue(draft.formData)

          // 再设置固件选择状态
          setSelectedFirmwareIds(draft.selectedFirmwareIds || [])
          setSelectedFirmwareInfo(
            (draft.selectedFirmwareIds || []).map((id: string) => ({
              firmwareId: id,
              moduleName: '',
              firmwareVersion: 0,
              remark: '',
            }))
          )

          setHasDraft(true)

          // 返回恢复的内容信息
          const hasFormData = Object.keys(draft.formData || {}).some(
            key =>
              draft.formData[key] !== undefined &&
              draft.formData[key] !== null &&
              draft.formData[key] !== ''
          )
          const hasFirmwareIds = (draft.selectedFirmwareIds || []).length > 0

          return {
            success: true,
            hasContent: hasFormData || hasFirmwareIds,
          }
        } else {
          // 草稿过期，清除
          clearDraft()
        }
      } catch (error) {
        console.error('加载草稿失败:', error)
        clearDraft()
      }
    }
    return { success: false, hasContent: false }
  }

  const clearDraft = () => {
    localStorage.removeItem(draftKey)
    setHasDraft(false)
  }

  // 获取Campaign列表
  const fetchCampaignList = async (page = currentPage, size = pageSize) => {
    try {
      setTableLoading(true)
      const params: any = {
        pageNum: page.toString(),
        pageSize: size.toString(),
      }

      const response = await getCampaignList(params)

      if (response.code === '200') {
        setCampaignList(response.data.list)
        setTotal(response.data.total || 0)
      } else {
        message.error(response.info)
      }
    } catch (error) {
      message.error('获取Campaign列表失败')
      console.error('获取Campaign列表失败:', error)
    } finally {
      setTableLoading(false)
    }
  }

  // 获取可用固件列表

  useEffect(() => {
    fetchCampaignList(1, pageSize)
    // 获取模块名列表
    getModuleNameList()
      .then(data => {
        setModuleNameList(data)
      })
      .catch(error => {
        console.error('获取模块名列表失败:', error)
        message.error('获取模块名列表失败')
      })
    // 获取项目阶段列表
    getProjectPhaseNameList()
      .then(data => {
        setProjectPhaseList(data)
      })
      .catch(error => {
        console.error('获取项目阶段列表失败:', error)
        message.error('获取项目阶段列表失败')
      })

    // 检查是否有草稿
    const draftStr = localStorage.getItem(draftKey)
    if (draftStr) {
      try {
        const draft = JSON.parse(draftStr)
        if (Date.now() - draft.timestamp < 24 * 60 * 60 * 1000) {
          setHasDraft(true)
        } else {
          clearDraft()
        }
      } catch (error) {
        clearDraft()
      }
    }
  }, [])

  // 移除自动实时保存，改为手动保存
  // 只在用户主动操作时保存草稿，避免自动保存覆盖问题

  // 页面卸载时保存草稿
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!editingCampaign && isModalVisible) {
        const formData = form.getFieldsValue()
        const validFormData = Object.keys(formData).reduce((acc, key) => {
          if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
            acc[key] = formData[key]
          }
          return acc
        }, {} as any)

        if (Object.keys(validFormData).length > 0 || selectedFirmwareIds.length > 0) {
          saveDraft(selectedFirmwareIds)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [editingCampaign, isModalVisible, selectedFirmwareIds, form])

  const handleAdd = async () => {
    setEditingCampaign(null)

    // 先打开弹窗，再处理草稿恢复
    setIsModalVisible(true)

    // 延迟处理草稿恢复，确保弹窗完全打开
    setTimeout(() => {
      if (hasDraft) {
        // 直接恢复草稿
        const draftResult = loadDraft()
        // 延迟再次检查表单状态，确保草稿完全恢复
        if (draftResult.success && draftResult.hasContent) {
          setTimeout(() => {
            const currentFormData = form.getFieldsValue()
            const currentFirmwareIds = selectedFirmwareIds

            // 如果发现草稿恢复不完整，重新保存一次
            if (
              Object.keys(currentFormData).some(key => currentFormData[key]) ||
              currentFirmwareIds.length > 0
            ) {
              saveDraft(currentFirmwareIds)
            }
          }, 500)
        }
      } else {
        // 没有草稿，正常重置
        setSelectedFirmwareIds([])
        setSelectedFirmwareInfo([])
        form.resetFields()
      }
    }, 100)
  }

  // 监听项目阶段选择变化，只更新选择状态，不自动获取固件列表
  const handlePhaseChange = (_selectedPhaseId: string | undefined) => {
    // 项目阶段变化时，清空之前选择的固件列表和可用的固件列表
    setSelectedFirmwareIds([])
    setSelectedFirmwareInfo([])
    setAvailableFirmwareList([])
    // 等待用户主动点击"选择小包固件"按钮时再获取固件列表
  }

  // 监听模块选择变化，只更新选择状态，不自动获取固件列表
  const handleModuleChange = (_selectedModules: string[]) => {
    // 模块选择变化时，清空之前选择的固件列表
    setSelectedFirmwareIds([])
    setSelectedFirmwareInfo([])
    // 清空可用的固件列表，等待用户主动点击"选择小包固件"按钮时再获取
    setAvailableFirmwareList([])
  }

  const handleEdit = async (record: CampaignItem) => {
    try {
      setLoading(true)

      const response = await getCampaignDetail(record.campaignId)
      if (response.code === '200' && response.data) {
        const detail = response.data
        setEditingCampaign(detail)

        // 处理新的firmwareInfoList数据结构
        if (detail.firmwareInfoList && detail.firmwareInfoList.length > 0) {
          const firmwareIds = detail.firmwareInfoList.map(item => item.firmwareId)
          const moduleNames = detail.firmwareInfoList.map(item => item.moduleName)
          setSelectedFirmwareIds(firmwareIds)

          // 回填显示固件信息
          const info = detail.firmwareInfoList.map(item => ({
            firmwareId: item.firmwareId,
            moduleName: item.moduleName,
            firmwareVersion: item.firmwareVersion,
            remark: item.remark || '',
          }))
          setSelectedFirmwareInfo(info)

          form.setFieldsValue({
            campaignName: detail.campaignName,
            version: convertNumberToVersion(detail.version || 100),
            status: mapApiStatusToUi(detail.status),
            moduleName: moduleNames,
            remark: detail.remark,
          })
        } else {
          // 兼容旧的数据结构
          setSelectedFirmwareIds(detail.firmwareIdList || [])
          const info = (detail.moduleNameList || []).map((name, idx) => ({
            firmwareId: detail.firmwareIdList?.[idx] || name,
            moduleName: name,
            firmwareVersion: 0,
            remark: '',
          }))
          setSelectedFirmwareInfo(info)
          form.setFieldsValue({
            campaignName: detail.campaignName,
            version: convertNumberToVersion(detail.version || 100),
            status: mapApiStatusToUi(detail.status),
            moduleName: detail.moduleNameList || [],
            remark: detail.remark,
          })
        }

        setIsModalVisible(true)
      } else {
        // 显示后端返回的具体错误信息
        message.error(response.info || '获取Campaign详情失败')
      }
    } catch (error) {
      console.error('获取Campaign详情失败:', error)
      message.error('获取Campaign详情失败')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (campaignId: string) => {
    try {
      setLoading(true)
      const res = await deleteCampaign(campaignId)
      if (res.code === '200') {
        message.success('删除成功')
        fetchCampaignList(currentPage, pageSize)
      } else {
        message.error(res.info || '删除失败')
      }
    } catch (error) {
      console.error('删除Campaign失败:', error)
      message.error('删除失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      if (selectedFirmwareIds.length === 0) {
        message.error('请选择至少一个固件')
        setLoading(false)
        return
      }

      if (editingCampaign) {
        // 编辑模式
        const res = await updateCampaign({
          campaignId: editingCampaign.campaignId,
          campaignName: values.campaignName,
          firmwareIdList: selectedFirmwareIds,
          version: convertVersionToNumber(values.version) || editingCampaign.version || 100,
          status: mapUiStatusToApi(values.status),
          remark: values.remark,
        })
        if (res.code === '200') {
          message.success('编辑成功')
          setIsModalVisible(false)
          form.resetFields()
          setSelectedFirmwareIds([])
          // 清除草稿
          clearDraft()
          fetchCampaignList(currentPage, pageSize)
        } else {
          message.error(res.info || '编辑失败')
        }
      } else {
        // 新增模式 - 使用正确的字段格式
        const requestData = {
          campaignName: values.campaignName,
          firmwareIdList: selectedFirmwareIds,
          version: convertVersionToNumber(values.version),
          status: mapUiStatusToApi(values.status),
          remark: values.remark,
        }

        const res = await createCampaign(requestData)
        if (res.code === '200') {
          message.success('新增成功')
          setIsModalVisible(false)
          form.resetFields()
          setSelectedFirmwareIds([])
          // 清除草稿
          clearDraft()
          fetchCampaignList(currentPage, pageSize)
        } else {
          message.error(res.info || '新增失败')
        }
      }
    } catch (error) {
      console.error('操作失败:', error)
      message.error('操作失败，请检查表单')
    } finally {
      setLoading(false)
    }
  }

  const handleModalCancel = () => {
    // 如果不是编辑模式，则保存草稿
    if (!editingCampaign) {
      const formData = form.getFieldsValue()
      // 只保存有值的字段
      const validFormData = Object.keys(formData).reduce((acc, key) => {
        if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
          acc[key] = formData[key]
        }
        return acc
      }, {} as any)

      // 如果有填写的内容，保存草稿
      if (Object.keys(validFormData).length > 0 || selectedFirmwareIds.length > 0) {
        saveDraft(selectedFirmwareIds)
      } else {
        // 如果没有内容，清除草稿
        clearDraft()
      }
    }

    setIsModalVisible(false)
    setSelectedFirmwareIds([])
    setEditingCampaign(null)
    form.resetFields()
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DEV':
        return '开发'
      case 'TEST':
        return '测试'
      default:
        return '测试' // 默认显示为测试状态
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DEV':
        return 'orange'
      case 'TEST':
        return 'blue'
      default:
        return 'blue' // 默认使用蓝色（测试状态的颜色）
    }
  }

  // UI状态值与API状态值映射
  const mapUiStatusToApi = (ui: string | undefined) => {
    return ui || 'TEST'
  }

  const mapApiStatusToUi = (api: string | undefined) => {
    return api || 'TEST'
  }

  // 版本号格式化函数（用于固件显示）
  const formatVersion = (version: string | number | undefined | null) => {
    if (version === undefined || version === null) {
      return 'V0.0.0'
    }
    const versionStr = version.toString()
    if (versionStr.length >= 3) {
      const major = versionStr.slice(0, -2)
      const minor = versionStr.slice(-2, -1)
      const patch = versionStr.slice(-1)
      return `V${major}.${minor}.${patch}`
    } else if (versionStr.length === 2) {
      return `V0.${versionStr.slice(0, 1)}.${versionStr.slice(1)}`
    } else {
      return `V0.0.${versionStr}`
    }
  }

  // 版本号转换函数：9.0.0 -> 900
  const convertVersionToNumber = (versionStr: string): number => {
    const parts = versionStr.split('.')
    if (parts.length === 3) {
      const major = parseInt(parts[0]) || 0
      const minor = parseInt(parts[1]) || 0
      const patch = parseInt(parts[2]) || 0
      return major * 100 + minor * 10 + patch
    }
    return 0
  }

  // 版本号转换函数：900 -> 9.0.0
  const convertNumberToVersion = (versionNum: number): string => {
    const major = Math.floor(versionNum / 100)
    const minor = Math.floor((versionNum % 100) / 10)
    const patch = versionNum % 10
    return `${major}.${minor}.${patch}`
  }

  const handleFirmwareSelection = async () => {
    // 检查是否已选择模块
    const selectedModules = form.getFieldValue('moduleName')
    if (!selectedModules || selectedModules.length === 0) {
      message.warning('请先选择模块')
      return
    }

    try {
      setFirmwareListLoading(true)

      // 获取当前选择的项目阶段
      const selectedPhaseId = form.getFieldValue('phaseId')

      // 根据选择的模块名称获取对应的模块ID
      const selectedModuleIds = selectedModules
        .map((moduleName: string) => {
          const module = moduleNameList.find(m => m.moduleName === moduleName)
          return module?.moduleId
        })
        .filter(Boolean) as string[]

      // 根据选择的模块和项目阶段获取对应的固件列表
      const phaseIds = selectedPhaseId
        ? [selectedPhaseId]
        : projectPhaseList.map(phase => phase.phaseId)

      const response = await getAvailableFirmwareList({
        moduleIds: selectedModuleIds,
        phaseIds: phaseIds,
      })

      if (response.code === '200') {
        const firmwareList = response.data || []
        setAvailableFirmwareList(firmwareList)

        // 检查是否有可用的固件
        if (firmwareList.length === 0) {
          message.warning('当前选择的模块没有可用的固件')
          return
        }

        // 打开固件选择模态框
        setIsFirmwareModalVisible(true)
      } else {
        message.error(response.info || '获取固件列表失败')
      }
    } catch (error) {
      console.error('获取固件列表失败:', error)
      message.error('获取固件列表失败')
    } finally {
      setFirmwareListLoading(false)
    }
  }

  const handleFirmwareSelect = (id: string) => {
    if (selectedFirmwareIds.includes(id)) {
      setSelectedFirmwareIds(selectedFirmwareIds.filter(firmwareId => firmwareId !== id))
    } else {
      setSelectedFirmwareIds([...selectedFirmwareIds, id])
    }
  }

  const handleRemoveFirmware = (id: string) => {
    setSelectedFirmwareIds(selectedFirmwareIds.filter(firmwareId => firmwareId !== id))
  }

  // 拖拽排序 - 记录拖拽起始索引
  const dragIndexRef = React.useRef<number | null>(null)

  const moveSelectedFirmware = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return
    setSelectedFirmwareIds(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }

  const getSelectedFirmwareList = () => {
    // 按selectedFirmwareIds的顺序返回列表
    const ordered = selectedFirmwareIds
      .map(id => availableFirmwareList.find(f => f.firmwareId === id))
      .filter(Boolean) as typeof availableFirmwareList
    if (ordered.length === 0) {
      return selectedFirmwareInfo
    }
    return ordered
  }

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => {
        // 计算当前页的序号
        const currentIndex = (currentPage - 1) * pageSize + index + 1
        return currentIndex
      },
    },
    {
      title: 'Campaign名称',
      dataIndex: 'campaignName',
      key: 'campaignName',
      width: 200,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className='status-tag'>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
      render: (remark: string) => remark || '-',
    },
    {
      title: '更新者',
      dataIndex: 'createBy',
      key: 'createBy',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (createTime: string) => formatToCST(createTime),
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 160,
      render: (updateTime: string) => formatToCST(updateTime),
    },
    {
      title: 'Campaign ID',
      dataIndex: 'campaignId',
      key: 'campaignId',
      width: 200,
      ellipsis: true,
      render: (campaignId: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{campaignId}</span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: CampaignItem) => {
        const actions = [
          <Button
            key='edit'
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={loading}
          >
            编辑
          </Button>,
        ]

        // 发布功能已取消，不显示发布按钮

        actions.push(
          <Popconfirm
            key='delete'
            title='确定要删除这个Campaign吗？'
            onConfirm={() => handleDelete(record.campaignId)}
            okText='确定'
            cancelText='取消'
          >
            <Button type='link' danger icon={<DeleteOutlined />} loading={loading}>
              删除
            </Button>
          </Popconfirm>
        )

        return <Space size='small'>{actions}</Space>
      },
    },
  ]

  return (
    <Spin spinning={loading}>
      <div className='campaign-list'>
        <div className='campaign-list-header'>
          <Title level={4}>Campaign管理</Title>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            新增Campaign
          </Button>
        </div>

        <Card className='campaign-list-card'>
          <Table
            columns={columns}
            dataSource={campaignList}
            rowKey='campaignId'
            loading={tableLoading}
            scroll={{ x: 1400 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number) => `共 ${total} 条记录`,
              pageSizeOptions: ['50', '100'],
              onChange: (page: number, size: number) => {
                setCurrentPage(page)
                setPageSize(size)
                fetchCampaignList(page, size)
              },
            }}
          />
        </Card>

        {/* Campaign编辑/新增模态框 */}
        <Modal
          title={editingCampaign ? '编辑Campaign' : '新增Campaign'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={800}
          okText='确定'
          cancelText='取消'
          confirmLoading={loading}
        >
          <Form form={form} layout='vertical'>
            <Form.Item
              name='campaignName'
              label='Campaign名称'
              rules={[
                {
                  required: true,
                  message: '请输入Campaign名称',
                },
              ]}
            >
              <Input placeholder='请输入Campaign名称' />
            </Form.Item>

            <Form.Item
              name='version'
              label='版本号'
              rules={[
                { required: true, message: '请输入版本号' },
                {
                  pattern: /^\d+(\.\d+)*$/,
                  message: '版本号格式必须为 X.X.X',
                },
              ]}
            >
              <Input placeholder='请输入版本号，如: 1.0.0' />
            </Form.Item>

            <Form.Item
              name='remark'
              label='备注'
              rules={[
                {
                  required: false,
                  message: '请输入备注',
                },
              ]}
            >
              <Input.TextArea
                placeholder='请输入备注信息（可选）'
                rows={3}
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item
              name='status'
              label='类型'
              rules={[
                {
                  required: true,
                  message: '请选择状态',
                },
              ]}
              initialValue='DEV'
            >
              <Select placeholder='请选择状态'>
                <Option value='DEV'>开发</Option>
                <Option value='TEST'>测试</Option>
              </Select>
            </Form.Item>

            <Form.Item name='phaseId' label='选择项目阶段（用于筛选固件）'>
              <Select placeholder='请选择阶段' allowClear onChange={handlePhaseChange}>
                {projectPhaseList.map(phase => (
                  <Option key={phase.phaseId} value={phase.phaseId}>
                    {phase.phaseName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='moduleName' label='选择模块（用于筛选固件）'>
              <Select
                mode='multiple'
                allowClear
                placeholder='请选择模块'
                maxTagCount='responsive'
                onChange={handleModuleChange}
              >
                {moduleNameList.map(module => (
                  <Option key={module.moduleId} value={module.moduleName}>
                    {module.moduleName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label='选择小包固件'>
              <div>
                <Button
                  type='dashed'
                  onClick={handleFirmwareSelection}
                  loading={firmwareListLoading}
                  disabled={firmwareListLoading}
                  style={{ width: '100%' }}
                >
                  {firmwareListLoading
                    ? '获取固件列表中...'
                    : `选择小包固件 (${selectedFirmwareIds.length} 个已选择)`}
                </Button>

                {selectedFirmwareIds.length > 0 && (
                  <div
                    className='selected-firmware-list'
                    style={{ marginTop: '20px', paddingTop: '16px' }}
                    ref={selectedListRef}
                    onDragOver={e => {
                      e.preventDefault()
                      // 拖拽到列表空白处，显示在末尾的占位
                      if (e.target === selectedListRef.current) {
                        setDragOverIndex(getSelectedFirmwareList().length)
                      }
                      // 自动滚动容器或窗口
                      const container = selectedListRef.current
                      if (container) {
                        const rect = container.getBoundingClientRect()
                        const edge = 60
                        const y = e.clientY
                        const nearTop = y < rect.top + edge
                        const nearBottom = y > rect.bottom - edge
                        if (nearTop) {
                          const canScrollUp = container.scrollTop > 0
                          if (canScrollUp) {
                            container.scrollTop -= 12
                          } else {
                            const scroller =
                              document.scrollingElement || document.documentElement || document.body
                            scroller.scrollTop -= 12
                          }
                        } else if (nearBottom) {
                          const canScrollDown =
                            container.scrollTop + container.clientHeight < container.scrollHeight
                          if (canScrollDown) {
                            container.scrollTop += 12
                          } else {
                            const scroller =
                              document.scrollingElement || document.documentElement || document.body
                            scroller.scrollTop += 12
                          }
                        }
                      }
                    }}
                    onDrop={e => {
                      e.preventDefault()
                      // 若拖拽到空白区域，移动到末尾
                      if (dragIndexRef.current != null) {
                        const targetIndex =
                          dragOverIndex != null ? dragOverIndex : selectedFirmwareIds.length - 1
                        moveSelectedFirmware(dragIndexRef.current, targetIndex)
                        dragIndexRef.current = null
                        setDragOverIndex(null)
                      }
                    }}
                  >
                    {getSelectedFirmwareList().map((firmware, index) => (
                      <React.Fragment key={firmware.firmwareId}>
                        {dragOverIndex === index && (
                          <div
                            style={{
                              height: 10,
                              border: '2px dashed #69c0ff',
                              borderRadius: 6,
                              margin: '8px 0',
                            }}
                          />
                        )}
                        <div
                          className='selected-item'
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: '#f0f9ff',
                            border: '1px solid #bae6fd',
                            borderRadius: '6px',
                            marginBottom: '12px',
                            gap: '24px',
                            cursor: 'grab',
                          }}
                          draggable
                          onDragStart={e => {
                            dragIndexRef.current = index
                            ;(e.currentTarget as HTMLDivElement).style.cursor = 'grabbing'
                          }}
                          onDragOver={e => {
                            e.preventDefault()
                            setDragOverIndex(index)
                            // 自动滚动容器或窗口
                            const container = selectedListRef.current
                            if (container) {
                              const rect = container.getBoundingClientRect()
                              const edge = 60
                              const y = e.clientY
                              const nearTop = y < rect.top + edge
                              const nearBottom = y > rect.bottom - edge
                              if (nearTop) {
                                const canScrollUp = container.scrollTop > 0
                                if (canScrollUp) {
                                  container.scrollTop -= 12
                                } else {
                                  const scroller =
                                    document.scrollingElement ||
                                    document.documentElement ||
                                    document.body
                                  scroller.scrollTop -= 12
                                }
                              } else if (nearBottom) {
                                const canScrollDown =
                                  container.scrollTop + container.clientHeight <
                                  container.scrollHeight
                                if (canScrollDown) {
                                  container.scrollTop += 12
                                } else {
                                  const scroller =
                                    document.scrollingElement ||
                                    document.documentElement ||
                                    document.body
                                  scroller.scrollTop += 12
                                }
                              }
                            }
                          }}
                          onDrop={e => {
                            e.preventDefault()
                            if (dragIndexRef.current != null) {
                              const target = dragOverIndex != null ? dragOverIndex : index
                              moveSelectedFirmware(dragIndexRef.current, target)
                              dragIndexRef.current = null
                              setDragOverIndex(null)
                            }
                            ;(e.currentTarget as HTMLDivElement).style.cursor = 'grab'
                          }}
                          onDragEnd={e => {
                            dragIndexRef.current = null
                            setDragOverIndex(null)
                            ;(e.currentTarget as HTMLDivElement).style.cursor = 'grab'
                          }}
                        >
                          <span
                            className='firmware-display-name'
                            style={{
                              fontWeight: '500',
                              color: '#0369a1',
                              fontSize: '14px',
                              flex: '1',
                            }}
                          >
                            {firmware.moduleName} - {formatVersion(firmware.firmwareVersion)}
                            {firmware.remark && ` - ${firmware.remark}`}
                          </span>
                          <span
                            className='remove-btn'
                            onClick={() => handleRemoveFirmware(firmware.firmwareId)}
                            style={{
                              color: '#dc2626',
                              cursor: 'pointer',
                              padding: '8px',
                              marginLeft: '24px',
                              borderRadius: '4px',
                              flexShrink: '0',
                            }}
                          >
                            <DeleteOutlined />
                          </span>
                        </div>
                      </React.Fragment>
                    ))}
                    {dragOverIndex === getSelectedFirmwareList().length && (
                      <div
                        style={{
                          height: 10,
                          border: '2px dashed #69c0ff',
                          borderRadius: 6,
                          margin: '8px 0',
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </Form.Item>
          </Form>
        </Modal>

        {/* 固件选择模态框 */}
        <Modal
          title='选择固件'
          open={isFirmwareModalVisible}
          onOk={() => setIsFirmwareModalVisible(false)}
          onCancel={() => setIsFirmwareModalVisible(false)}
          width={600}
          okText='确定'
          cancelText='取消'
          bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
        >
          <div className='firmware-selection'>
            {availableFirmwareList.length > 0 ? (
              availableFirmwareList.map(firmware => (
                <div
                  key={firmware.firmwareId}
                  className='firmware-item'
                  onClick={() => handleFirmwareSelect(firmware.firmwareId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: selectedFirmwareIds.includes(firmware.firmwareId)
                      ? '#f0f9ff'
                      : undefined,
                    border: selectedFirmwareIds.includes(firmware.firmwareId)
                      ? '1px solid #bae6fd'
                      : '1px solid transparent',
                    borderRadius: '6px',
                  }}
                >
                  <Checkbox
                    checked={selectedFirmwareIds.includes(firmware.firmwareId)}
                    style={{ marginRight: '12px' }}
                  />
                  <span
                    className='firmware-name'
                    style={{
                      fontWeight: '500',
                      color: '#1f2937',
                      fontSize: '14px',
                    }}
                  >
                    {firmware.moduleName} - {formatVersion(firmware.firmwareVersion)}
                    {firmware.remark && ` - ${firmware.remark}`}
                  </span>
                </div>
              ))
            ) : (
              <Empty description='暂无可用固件' />
            )}
          </div>
        </Modal>
      </div>
    </Spin>
  )
}

export default CampaignList
